/**
 * L'abbonamento è fermo: al posto dell'app, la pagina per ripartire.
 *
 * Come fanno i servizi in abbonamento: chi non paga entra lo stesso, ma vede
 * solo questo. Prima veniva rifiutato già alla porta, e non poteva nemmeno
 * arrivare al bottone per pagare.
 *
 * Tre cose, e nessuna di più: riattivare (un Checkout di Stripe sullo stesso
 * cliente), portarsi via i propri dati — un diritto che non dipende
 * dall'abbonamento — e uscire.
 *
 * Al ritorno da Stripe (`?riattivato=1`) il webhook può arrivare un attimo
 * dopo il browser: invece di mostrare di nuovo «non attivo» a chi ha appena
 * pagato, si aspetta, ricontrollando, e si apre l'app appena è pronta.
 */

import { useEffect, useState } from "react";
import { Download, LogOut, RefreshCw } from "lucide-react";
import { todayInRome } from "@otto/shared";
import { api, setToken } from "../lib/api.ts";
import { messaggioDi } from "../lib/errori.ts";
import { Pill } from "../ui/primitives.tsx";
import { Logo } from "./Accesso.tsx";

/** Quanto si aspetta il webhook prima di dire di ricaricare. */
const ATTESA_MASSIMA_MS = 40_000;
const OGNI_MS = 2_000;

function perche(abbonamento: string | null): string {
  switch (abbonamento) {
    case "canceled":
      return "L'abbonamento è stato disdetto.";
    case "unpaid":
      return "Gli ultimi pagamenti non sono andati a buon fine, e Stripe ha smesso di riprovare.";
    case "incomplete":
    case "incomplete_expired":
      return "Il primo pagamento non si è completato.";
    case "paused":
      return "L'abbonamento è in pausa.";
    default:
      return "L'abbonamento non è attivo.";
  }
}

export function Riattiva({ abbonamento }: { abbonamento: string | null }) {
  const [inAttesa, setInAttesa] = useState(() =>
    new URLSearchParams(window.location.search).has("riattivato"),
  );
  const [scaduta, setScaduta] = useState(false);
  const [apro, setApro] = useState(false);
  const [scarico, setScarico] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  // Di ritorno dal pagamento: si ricontrolla finché il webhook non arriva.
  useEffect(() => {
    if (!inAttesa) return;
    let vivo = true;
    const inizio = Date.now();
    const giro = async () => {
      while (vivo && Date.now() - inizio < ATTESA_MASSIMA_MS) {
        await new Promise((fatto) => setTimeout(fatto, OGNI_MS));
        const me = await api.me().catch(() => null);
        if (me && !me.account.bloccatoPerAbbonamento) {
          window.location.replace(window.location.pathname);
          return;
        }
      }
      if (vivo) setScaduta(true);
    };
    void giro();
    return () => {
      vivo = false;
    };
  }, [inAttesa]);

  const riattiva = async () => {
    setApro(true);
    setErrore(null);
    try {
      const { url } = await api.riattivaAbbonamento();
      window.location.assign(url);
    } catch (e) {
      setErrore(messaggioDi(e));
      setApro(false);
    }
  };

  const scarica = async () => {
    setScarico(true);
    setErrore(null);
    try {
      const dati = await api.esportaDati();
      const blob = new Blob([JSON.stringify(dati, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `otto-dati-${todayInRome()}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErrore(messaggioDi(e));
    } finally {
      setScarico(false);
    }
  };

  const esci = () => {
    setToken(null);
    window.location.reload();
  };

  return (
    <div className="flex h-full flex-col overflow-y-auto bg-surface px-6 py-8 sm:px-12">
      <div className="flex items-center gap-2">
        <Logo />
        <span className="text-[19px] font-extrabold tracking-tight text-ink">
          Otto
        </span>
      </div>

      <div className="my-auto w-full max-w-[480px] self-center py-12">
        {inAttesa && !scaduta ? (
          <div className="animate-rise">
            <h1 className="text-[28px] font-extrabold leading-tight tracking-tight text-ink">
              Pagamento ricevuto
            </h1>
            <p className="mt-3 text-[15px] leading-relaxed text-ink-soft">
              Sto riattivando Otto: di solito ci vogliono pochi secondi.
            </p>
            <p className="mt-6 flex items-center gap-2 text-[13.5px] font-semibold text-ink-soft">
              <RefreshCw size={15} className="animate-spin" />
              Controllo l'abbonamento…
            </p>
          </div>
        ) : (
          <div className="animate-rise">
            <h1 className="text-[28px] font-extrabold leading-tight tracking-tight text-ink">
              {scaduta
                ? "Il pagamento non risulta ancora"
                : "Il tuo abbonamento non è attivo"}
            </h1>
            <p className="mt-3 text-[15px] leading-relaxed text-ink-soft">
              {scaduta
                ? "Stripe non ci ha ancora confermato il pagamento. Se hai appena pagato, ricarica la pagina fra un minuto; se non torna, scrivici a ciao@ottoguest.com."
                : `${perche(abbonamento)} Otto è fermo: non legge e non scrive su Guesty.`}
            </p>

            <div className="mt-5 rounded-card border border-line bg-surface-muted px-4 py-3.5 text-[13.5px] leading-relaxed text-ink-soft">
              I tuoi dati sono al sicuro: case, conti, conversazioni e le
              risposte che Otto ha imparato restano qui. Riattivando riparte
              tutto da dove l'hai lasciato.
            </div>

            <div className="mt-7 flex flex-col gap-2.5">
              <Pill
                variant="primary"
                size="md"
                block
                onClick={() => {
                  if (scaduta) window.location.reload();
                  else void riattiva();
                }}
                loading={apro}
              >
                {scaduta ? "Ricarica" : "Riattiva Otto"}
              </Pill>
              <Pill
                variant="secondary"
                size="md"
                block
                icon={<Download size={15} />}
                onClick={() => void scarica()}
                loading={scarico}
              >
                Scarica i tuoi dati
              </Pill>
              <Pill
                variant="ghost"
                size="md"
                block
                icon={<LogOut size={15} />}
                onClick={esci}
              >
                Esci
              </Pill>
            </div>

            {errore ? (
              <p className="mt-4 text-[13.5px] font-semibold text-danger">
                {errore}
              </p>
            ) : null}
            {inAttesa && scaduta ? (
              <button
                type="button"
                onClick={() => {
                  setInAttesa(false);
                  setScaduta(false);
                }}
                className="mt-4 text-[13px] font-semibold text-ink-soft underline underline-offset-2"
              >
                Torna alla riattivazione
              </button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Pagamento in ritardo: l'app resta aperta perché Stripe sta ritentando, ma
 * l'host deve saperlo prima che i tentativi finiscano e Otto si fermi.
 */
export function AvvisoPagamento() {
  const [apro, setApro] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  const aggiorna = async () => {
    setApro(true);
    setErrore(null);
    try {
      const { url } = await api.portaleAbbonamento();
      window.location.assign(url);
    } catch (e) {
      setErrore(messaggioDi(e));
      setApro(false);
    }
  };

  return (
    <div
      role="alert"
      className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-line bg-warn-soft px-5 py-2.5 text-[13.5px] text-ink"
    >
      <p className="min-w-0 flex-1">
        <b>L'ultimo pagamento non è passato.</b> Stripe sta riprovando: aggiorna
        la carta per non fermare Otto.
        {errore ? <span className="ml-2 text-danger">{errore}</span> : null}
      </p>
      <Pill
        size="sm"
        variant="primary"
        onClick={() => void aggiorna()}
        loading={apro}
      >
        Aggiorna la carta
      </Pill>
    </div>
  );
}
